import { useCallback, useRef } from "react";

/**
 * Returns an immutable callback (i.e. that does not have a dependencies), which wraps the passed
 * `cb`, which can be mutable.
 *
 * @param cb Any function to be wrapped in the resulting immutable function.
 */
export const useImmutableCallback = <C extends AnyFunction>(cb: C) => {
    const ref = useRef(cb);

    // the ingoing function can change on every render,
    // thus the resulting callback propagates the call to the most recent one.
    ref.current = cb;

    // use the function instead of a thick-arrow function to get `this` context for subclassing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useCallback<C>(
        function (this: ThisParameterType<C>, ...args: Parameters<C>) {
            return ref.current?.apply(this, args);
        } as C,
        []
    );
};
